import { memo, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { parseApiError } from "app/services/api.error.parser";
import { addClientMappingToBackend, updateClientMappingToBackend } from "app/services/client.service";
import MDBox from "components/MDBox";
import MasterDataSelect from "app/components/shared/MasterDataSelect";
import { validateClientMappingObject } from "utils/clientValidationUtil";
import { useSelector } from "react-redux";

const obj = {
  userId: null,
  clientId: null,
};

const AddClientMappingDialog = ({ open, setOpen, refreshActivitiesDataHandler, editObject }) => {
  const [message, setMessage] = useState("");
  const [messageOpen, setMessageOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" or "warning" or "info" or "success"
  const [data, setData] = useState(obj);
  const clients = useSelector((state) => state.client.rows);
  const users = useSelector((state) => state.user.rows);
  const client_users = users.filter(user => user.type === 'CLIENT'); 

  useEffect(() => {
    setMessage("");
    setMessageOpen(false);
    setSeverity("info");
    if (!open) return;
    if (editObject) {
      setData({
        userId: parseInt(editObject.userId),
        clientId: parseInt(editObject.clientId),
      });
    } else {
      setData(obj);
    }
  }, [open, editObject]);

  const showBackendError = (error, fallback) => {
    const parsed = parseApiError(error);
    if (parsed.isUnauthorized) {
      handleAxiosError(error);
      return;
    }
    setErrorAction(parsed.message || fallback);
  };

  const saveClientMappingInBackend = async () => {
    if (validateClientMappingObject(data) && !editObject) await addClientMappingInvoker(data);
    else if (validateClientMappingObject(data) && editObject) await updateClientMappingInvoker(data);
    else {
      setErrorAction("One or more required fields for a Client are missing or having invalid value");
    }
  };

  const addClientMappingInvoker = async (clientObject) => {
    await addClientMappingToBackend(clientObject)
      .then((data) => {
        setSuccessAction("Client added successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to add client mapping. Please try again.");
      });
  };

  const updateClientMappingInvoker = async (data) => {
    const editObjectId = data.id;
    await updateClientMappingToBackend(editObjectId, data)
      .then((data) => {
        setSuccessAction("Client Updated successfully");
        setTimeout(() => {
          setData(obj);
          setMessageOpen(false);
          setMessage("");
          setOpen(false);
          refreshActivitiesDataHandler();
        }, 1000);
      })
      .catch((error) => {
        showBackendError(error, "Failed to update client mapping. Please try again.");
      });
  };

  const setErrorAction = (errorMessage) => {
    setMessage(errorMessage);
    setMessageOpen(true);
    setSeverity("error");
  };

  const setSuccessAction = (message) => {
    setMessage(message);
    setMessageOpen(true);
    setSeverity("success");
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth="sm">
      <DialogTitle>{editObject ? `Update Client Manager` : `Add New Client Manager`}</DialogTitle>
      <DialogContent>
        {/* <DialogContentText></DialogContentText> */}
        <MDBox my={2}>
          <MasterDataSelect
            id="client"
            label="Client"
            placeholder="Select a client"
            options={clients}
            value={data?.clientId || ""}
            onChange={(e) => setData({ ...data, clientId: parseInt(e.target.value, 10) || null })}
            helperText="*Required"
          />
        </MDBox>
        <MDBox my={2}>
          <MasterDataSelect
            id="user"
            label="User"
            placeholder="Select a user"
            options={client_users}
            getOptionLabel={(client_user) => client_user.fullName}
            value={data?.userId || ""}
            onChange={(e) => setData({ ...data, userId: parseInt(e.target.value, 10) || null })}
            helperText="*Required"
          />
        </MDBox>
        <Collapse in={messageOpen}>
          <Alert
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => {
                  setMessageOpen(false);
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
            severity={severity}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Cancel</Button>
        <Button onClick={saveClientMappingInBackend} variant="contained">
          {editObject ? `Update` : `Add`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddClientMappingDialog);
